/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, Button, Flex, IconButton, Stack, Text} from '@chakra-ui/react'
import {useToast} from '../../hooks/use-toast'
import {useForm} from 'react-hook-form'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import PromoCodeFields from '../../components/forms/promo-code-fields'
import {API_ERROR_MESSAGE} from '../../constants'
import Checkbox from '../checkbox'
import {CloseIcon, DeleteIcon, DiscountIcon} from '../icons'
import {trackEvent} from '../../tracking/analytics'
import {ShopperBasketsMutations, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'

export const usePromoCode = () => {
    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const form = useForm()
    const toast = useToast()
    const applyPromoCodeMutation = useShopperBasketsMutation(
        ShopperBasketsMutations.AddCouponToBasket
    )
    const removePromoCodeMutation = useShopperBasketsMutation(
        ShopperBasketsMutations.RemoveCouponFromBasket
    )

    const submitPromoCode = async ({code}) => {
        try {
            await applyPromoCodeMutation.mutateAsync({
                body: {code},
                parameters: {basketId: basket.basketId}
            })
            form.reset({code: ''})

            trackEvent({
                event: 'add_coupon',
                event_category: 'coupon',
                event_action: 'added',
                event_label: code
            })

            toast({
                title: formatMessage({
                    defaultMessage: 'Promotion applied',
                    id: 'use_promocode.info.promo_applied'
                })
            })
        } catch {
            form.setError('code', {
                type: 'manual',
                message: formatMessage({
                    defaultMessage:
                        'Please check the code and try again. It might have already been used or the action may have expired.',
                    id: 'use_promocode.error.check_the_code'
                })
            })
            form.setFocus('code')
        }
    }

    const removePromoCode = async (couponItemId) => {
        try {
            await removePromoCodeMutation.mutateAsync({
                parameters: {basketId: basket.basketId, couponItemId}
            })
            toast({
                title: formatMessage({
                    defaultMessage: 'Promotion removed',
                    id: 'use_promocode.info.promo_removed'
                })
            })
        } catch {
            toast({
                title: formatMessage(API_ERROR_MESSAGE),
                status: 'error'
            })
        }
    }

    return {form, submitPromoCode, removePromoCode}
}

export const PromoCodeCheck = (props) => {
    return (
        <Checkbox variant="link" name="hasPromoCode" {...props}>
            <FormattedMessage
                defaultMessage="Add a promo code"
                id="promocode.accordion.button.have_promocode"
            />
        </Checkbox>
    )
}

export const PromoCodeForm = ({form, submitPromoCode, ...props}) => {
    return (
        <Box
            data-testid="promo-code-form"
            as="form"
            onSubmit={form.handleSubmit(submitPromoCode)}
            {...props}
        >
            <PromoCodeFields form={form} />
        </Box>
    )
}

PromoCodeForm.propTypes = {
    form: PropTypes.object.isRequired,
    submitPromoCode: PropTypes.func.isRequired
}

export const PromoCodeItem = ({item, colorScheme}) => {
    const {removePromoCode} = usePromoCode()
    const {formatMessage} = useIntl()
    return (
        <Flex
            justifyContent="space-between"
            alignSelf="stretch"
            bg={`${colorScheme}.mid`}
            color={`${colorScheme}.dark`}
            padding={4}
            gap={2}
        >
            <Flex width="100%" justifyContent="center" gap={3}>
                <DiscountIcon boxSize={4} />
                <Text align="center" textTransform="uppercase" textStyle="Semi Bold Medium">
                    {item.code}
                </Text>
            </Flex>
            <IconButton
                variant="unstyled"
                lineHeight="normal"
                height={4}
                icon={<CloseIcon color={`${colorScheme}.dark`} boxSize={3.5} />}
                onClick={() => removePromoCode(item.couponItemId)}
                aria-label={formatMessage({
                    id: 'global.remove',
                    defaultMessage: 'Remove'
                })}
            />
        </Flex>
    )
}

PromoCodeItem.propTypes = {
    item: PropTypes.object.isRequired,
    colorScheme: PropTypes.string.isRequired
}

export const PromoCodeRemoveItem = ({onClick}) => {
    return (
        <Button variant="link" leftIcon={<DeleteIcon boxSize={4} />} onClick={onClick}>
            <FormattedMessage defaultMessage="Remove" id="global.remove" />
        </Button>
    )
}

PromoCodeRemoveItem.propTypes = {
    onClick: PropTypes.func
}

export const PromoCode = ({form, submitPromoCode}) => {
    const [isOpen, setOpen] = useState(false)
    const {data: basket} = useCurrentBasket()

    useEffect(() => {
        if (form.formState.isSubmitSuccessful) {
            setOpen(false)
        }
    }, [form.formState.isSubmitSuccessful])

    return (
        (!basket.couponItems || basket.couponItems?.length === 0) && (
            <Stack spacing={4} data-testid="add-promo-code-section">
                <PromoCodeCheck isChecked={isOpen} onChange={(e) => setOpen(e.target.checked)} />

                {isOpen && <PromoCodeForm form={form} submitPromoCode={submitPromoCode} pb={4} />}
            </Stack>
        )
    )
}

PromoCode.propTypes = {
    /** The form object returned from `usePromoCode` hook */
    form: PropTypes.object.isRequired,

    /** The submit callback returned from `usePromoCode` hook */
    submitPromoCode: PropTypes.func.isRequired,

    /** Props applied to inner AccordionItem. Useful for style overrides. */
    itemProps: PropTypes.object
}

export default PromoCode
